import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams, useOutletContext } from "react-router-dom";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";
import ScenarioTable from "./components/scenario-table";
import ScenarioPage from "./components/scenario-page";
import useNotification from "../../../components/notification/notification";
import { isReferencedError, hasReferencedFailure } from "../../../common/utils/referenced-error";
import { Body } from "../../../components/main-body/body";
import "./styles.css";
import ApiScenariosManagement from "../../../api/scenarios-management";
import DropdownFilters from "../../../components/dropdown-filters/dropdown-filters";
import { useBulkSelection } from "../../../common/utils/useBulkSelection";
import BulkSelectionBar from "../../../components/table/bulk-selection-bar";
import useAdminEvents from "../../../hooks/useAdminEvents";
import { useCan } from "../../../common/utils/usePermissions";

const ALLOWED_STATUSES = new Set(["active", "pending", "inactive"]);

function parseStatusesFromSearch(search) {
  const sp = new URLSearchParams(search || "");

  // ?statuses=pending
  // ?statuses=pending,active
  // ?statuses=pending&statuses=active
  const rawAll = sp.getAll("statuses").flatMap((v) => String(v).split(","));

  const statuses = rawAll
    .map((v) => v.trim())
    .filter(Boolean)
    .filter((v) => ALLOWED_STATUSES.has(v));

  return statuses.length ? statuses : null;
}

const ScenarioManagement = ({ mode }) => {
  const [scenarios, setScenarios] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState({});
  const [tasks, setTasks] = useState([]);
  const { t } = useTranslation();
  // LF360-2024: creating needs `content.edit` on the server.
  const canCreate = useCan("content.edit");
  const { openNotification } = useNotification();
  const [isBulkDeleting, setBulkDeleting] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { id } = useParams();
  const navigate = useProjectNavigate();
  const location = useLocation();

  const [filters, setFilters] = useState(() => {
    const statusesFromUrl = parseStatusesFromSearch(location.search);
    return statusesFromUrl ? { statuses: statusesFromUrl } : {};
  });

  const [sort, setSort] = useState({ sortField: null, sortOrder: null });
  const [changeLogs, setChangeLogs] = useState({});

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setLoading] = useState(false);

  const listReqIdRef = useRef(0);

  const { selectedKeys, clearSelection, getSelectionColumn, setSelectedKeys } = useBulkSelection(scenarios, {
    rowKey: "id",
  });

  const tableRef = useRef(null);
  const [bulkLeft, setBulkLeft] = useState(null);

  useEffect(() => {
    const updatePosition = () => {
      if (!tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      setBulkLeft(rect.left + rect.width / 2);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  const mergeUniqueById = (prev, incoming) => {
    const seen = new Set((prev || []).map((i) => i.id));
    return (prev || []).concat((incoming || []).filter((i) => !seen.has(i.id)));
  };

  const outlet = useOutletContext();
  const setSidebarProps = outlet?.setSidebarProps ? outlet.setSidebarProps : () => {};

  const handleNavigate = useCallback(
    (path) => {
      if (location.pathname !== path) navigate(path);
    },
    [location.pathname, navigate]
  );

  const syncScenarioInList = useCallback((scenario) => {
    if (!scenario?.id) return;

    setScenarios((prev) =>
      prev.map((item) =>
        String(item.id) === String(scenario.id)
          ? {
              ...item,
              title: scenario.title,
              status: scenario.status,
              updatedAt: scenario.updatedAt,
              createdAt: scenario.createdAt,
              ScenarioVersion: scenario.ScenarioVersion || item.ScenarioVersion,
            }
          : item
      )
    );
  }, []);

  const syncSelectedScenarioMetadata = useCallback((scenario) => {
    if (!scenario?.id) return;

    setSelectedScenario((prev) => {
      if (!prev?.id || String(prev.id) !== String(scenario.id)) return prev;

      return {
        ...prev,
        title: scenario.title,
        status: scenario.status,
        updatedAt: scenario.updatedAt,
        createdAt: scenario.createdAt,
        ScenarioVersion: scenario.ScenarioVersion || prev.ScenarioVersion,
      };
    });
  }, []);

  const fetchScenario = useCallback(
    async (scenarioId) => {
      if (!scenarioId) return null;

      const resp = await ApiScenariosManagement.getScenarioById(scenarioId);
      const full =
        resp?.data?.scenario ||
        resp?.data?.data?.scenario ||
        null;

      const trails =
        resp?.data?.changeLogs ||
        resp?.data?.changeLogsByScenario ||
        [];

      if (full) {
        setSelectedScenario(full);
        syncScenarioInList(full);
        setChangeLogs((prev) => ({ ...prev, [String(scenarioId)]: trails }));
      }

      return full;
    },
    [syncScenarioInList]
  );

  // Sidebar title/back
  useEffect(() => {
    if (selectedScenario?.id) {
      setSidebarProps({
        backOnly: true,
        onBack: () => handleNavigate(`/content/scenarios`),
      });
    } else {
      setSidebarProps({ title: "Content", backOnly: false, onBack: undefined });
    }
    // Reset on unmount so the Content submenu isn't left hidden (backOnly)
    // when navigating to another Content tab while viewing a scenario.
    return () => {
      setSidebarProps({ title: "Content", backOnly: false, onBack: undefined });
    };
  }, [selectedScenario?.id, setSidebarProps, handleNavigate]);

  const loadPage = useCallback(
    async (nextPage, { replace = false } = {}) => {
      if (isLoading && !replace) return;

      setLoading(true);
      try {
        const myReqId = ++listReqIdRef.current;

        const { data } = await ApiScenariosManagement.getScenariosLite({
          query: searchValue || "",
          ...filters,
          ...sort, // { sortField, sortOrder }
          page: nextPage,
        });

        if (myReqId !== listReqIdRef.current) return;

        const incomingScenarios = data?.scenarios || [];
        const incomingAuthors = data?.authors || [];

        setScenarios((prev) => (replace ? incomingScenarios : mergeUniqueById(prev, incomingScenarios)));
        setAuthors((prev) => (replace ? incomingAuthors : mergeUniqueById(prev, incomingAuthors)));

        setHasMore(Boolean(data?.hasMore));
        setPage(Number.isFinite(data?.page) ? data.page : nextPage);
      } catch (e) {
        console.log("Scenarios fetch error", e);
      } finally {
        setLoading(false);
      }
    },
    [filters, sort, searchValue, isLoading]
  );

  const resetAndFetch = useCallback(async () => {
    setPage(0);
    setHasMore(true);
    setScenarios([]);
    setAuthors([]);
    await loadPage(1, { replace: true });
  }, [loadPage]);

  const handleScenarioChanged = useCallback(
    (payload) => {
      const deletedIds = [
        ...(payload?.deletedIds || []),
        payload?.id,
      ].filter(Boolean).map(String);

      if (
        (payload?.action === "deleted" || payload?.action === "bulk_deleted") &&
        selectedScenario?.id &&
        deletedIds.includes(String(selectedScenario.id))
      ) {
        setSelectedScenario({});
        handleNavigate(`/content/scenarios`);
      }

      if (payload?.scenario) {
        syncScenarioInList(payload.scenario);
        syncSelectedScenarioMetadata(payload.scenario);
      }

      resetAndFetch();
    },
    [
      selectedScenario?.id,
      handleNavigate,
      resetAndFetch,
      syncScenarioInList,
      syncSelectedScenarioMetadata,
    ]
  );

  useAdminEvents({
    onScenarioChanged: handleScenarioChanged,
  });

  useEffect(() => {
    resetAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort, searchValue]);

  const fetchNext = () => {
    if (!hasMore || isLoading) return;
    return loadPage(page + 1);
  };

  const updateSorting = (sortField, sortOrder) => {
    setSort({ sortField, sortOrder });
  };

  const handleUpdateSearchValue = (value) => {
    setSearchValue(value);
  };

  const updateFilterValue = (value) => setFilters(value);

  const getDailyTasks = async () => {
    return ApiScenariosManagement.getDailyTasks().then((r) => {
      setTasks(r.data.dailyTasks.filter((task) => task.isActive === true));
    });
  };

  useEffect(() => {
    getDailyTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort]);

  useEffect(() => {
    if (!id) {
      if (selectedScenario?.id) setSelectedScenario({});
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const full = await fetchScenario(id);
        if (cancelled) return;

        if (full) {
          handleNavigate(`/content/scenarios/${id}`);
        } else if (mode !== "show") {
          setSelectedScenario({});
        }
      } catch (e) {
        if (!cancelled && mode !== "show") setSelectedScenario({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, fetchScenario, handleNavigate, mode, selectedScenario?.id]);

  const handleSelectScenario = (scenario) => {
    handleNavigate(`/content/scenarios/${scenario.id}`);
  };

  const handleDeselectScenario = async () => {
    setSelectedScenario({});
    handleNavigate(`/content/scenarios`);
    await resetAndFetch();
  };

  const createScenario = () => {
    setLoading(true);
    ApiScenariosManagement.createScenario()
      .then(async (r) => {
        openNotification({
          type: "success",
          message: (
            <p>
              {t("administration.scenario.notification.created", {
                scenario: r.data.scenario.title,
              })}
            </p>
          ),
        });

        const created = r.data.scenario;
        if (created?.id) {
          handleNavigate(`/content/scenarios/${created.id}`);
          await fetchScenario(created.id);
        }
      })
      .finally(async () => {
        setLoading(false);
        await resetAndFetch();
      });
  };

  const deleteScenario = (scenarioId, value) => {
    setLoading(true);
    ApiScenariosManagement.deleteScenario(scenarioId)
      .then(() => {
        openNotification({
          type: "success",
          message: (
            <p>
              {t("administration.scenario.notification.deleted", {
                scenario: value,
              })}
            </p>
          ),
        });
      })
      .catch((err) => {
        openNotification({
          type: "error",
          message: (
            <p>
              {isReferencedError(err)
                ? t("common.referenced_delete_error")
                : t("administration.scenario.notification.not_implemented", {
                    scenario: "",
                  })}
            </p>
          ),
        });
      })
      .finally(async () => {
        setLoading(false);
        if (String(id) === String(scenarioId)) {
          setSelectedScenario({});
          handleNavigate(`/content/scenarios`);
        }
        await resetAndFetch();
      });
  };

  const handleBulkDelete = useCallback(async () => {
    if (selectedKeys.length === 0 || isBulkDeleting) return;

    setBulkDeleting(true);

    try {
      const resp = await ApiScenariosManagement.removeBulk(selectedKeys);
      const { deletedIds = [], failed = [] } = resp?.data || {};
      const failedIds = failed.map((f) => f.id);

      if (deletedIds.length) {
        const plural = deletedIds.length > 1;
        openNotification?.({
          type: "success",
          message: <p>{deletedIds.length} {plural ? "scenarios were" : "scenario was"} deleted</p>,
        });
      }

      if (failedIds.length) {
        const plural = failedIds.length > 1;
        openNotification?.({
          type: "error",
          message: hasReferencedFailure(failed) ? (
            <p>{t("common.referenced_delete_error")}</p>
          ) : (
            <p>Unable to delete {failedIds.length} {plural ? "scenarios" : "scenario"}</p>
          ),
        });
      }

      if (!failedIds.length) {
        clearSelection();
      } else {
        setSelectedKeys(failedIds);
      }

      await resetAndFetch();
    } catch (e) {
      console.error(e);
      openNotification?.({
        type: "error",
        message: <p>Failed to delete scenarios</p>,
      });
    } finally {
      setBulkDeleting(false);
    }
  }, [
    selectedKeys,
    isBulkDeleting,
    openNotification,
    resetAndFetch,
    clearSelection,
    setSelectedKeys,
  ]);

  const handleDuplicate = (scenario) => {
    setLoading(true);

    ApiScenariosManagement.duplicateScenario(scenario.id)
      .then(() => {
        openNotification({
          type: "success",
          message: (
            <p>
              {t("administration.scenario.notification.duplicated", {
                scenario: scenario.title,
              })}
            </p>
          ),
        });
      })
      .catch(() => {
        openNotification({
          type: "error",
          message: (
            <p>
              {t("administration.scenario.notification.not_implemented", {
                scenario: "",
              })}
            </p>
          ),
        });
      })
      .finally(async () => {
        setLoading(false);
        await resetAndFetch();
      });
  };

  const updateScenarioAuthor = async (scenarioVersionId, authorId) => {
    try {
      await ApiScenariosManagement.updateScenarioVersion(scenarioVersionId, { userId: authorId });
    } catch (e) {
      // no-op
    }
  };

  const headerProps = useMemo(
    () => ({
      title: "Scenario management",
      create: canCreate && {
        label: t("administration.scenario.create"),
        onClick: createScenario,
      },
      search: {
        value: searchValue,
        onHandleChange: handleUpdateSearchValue,
      },
    }),
    [searchValue, t, createScenario, canCreate]
  );

  const statuses = [
    { id: "active", name: "Active" },
    { id: "pending", name: "Pending" },
    { id: "inactive", name: "Inactive" },
  ];

  return (
    <Body
      header={headerProps}
      filters={
        <DropdownFilters
          mode="controlled"
          filter={filters}
          filtersBy={[
            { name: "Author", id: "userIds", children: authors },
            { name: "Status", id: "statuses", children: statuses },
          ]}
          updateFilterValue={updateFilterValue}
        />
      }
    >
      {selectedScenario?.id ? (
        <ScenarioPage
          scenario={selectedScenario}
          toList={handleDeselectScenario}
          refreshScenario={() => fetchScenario(selectedScenario.id)}
          refreshList={resetAndFetch}
          openNotification={openNotification}
          api={ApiScenariosManagement}
          tasks={tasks}
          authors={authors}
          updateScenarioAuthor={updateScenarioAuthor}
          changeLogs={changeLogs}
        />
      ) : (
        <>
          <ScenarioTable
            updateSorting={updateSorting}
            data={scenarios}
            onHandleRowClick={handleSelectScenario}
            deleteScenario={deleteScenario}
            handleDuplicate={handleDuplicate}
            loading={isLoading}
            hasMore={hasMore}
            fetchData={fetchNext}
            selectionColumn={getSelectionColumn()}
            containerRef={tableRef}
          />
          <BulkSelectionBar
            left={bulkLeft}
            count={selectedKeys.length}
            onClear={clearSelection}
            onDelete={handleBulkDelete}
            loading={isBulkDeleting}
          />
        </>
      )}
    </Body>
  );
};

export default ScenarioManagement;
